import { type AxiosInstance } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { type FeatureFlagKey } from '../constants';
import { getFeatureFlag } from '../services';
import { Logger, getFeatureFlagFromCache } from '../utils';

interface IUseFeatureFlagReturn {
  isEnabled: boolean;
  isLoading: boolean;
  error: string | null;
}

interface IUseFeatureFlagProps {
  api: AxiosInstance;
  flagKey: FeatureFlagKey;
  isUserLogged?: boolean;
}

export const useFeatureFlag = ({
  api,
  flagKey,
  isUserLogged = true,
}: IUseFeatureFlagProps): IUseFeatureFlagReturn => {
  const cachedFlag = getFeatureFlagFromCache(flagKey);
  const [isEnabled, setIsEnabled] = useState(cachedFlag?.active ?? false);
  const [isLoading, setIsLoading] = useState(cachedFlag === null);
  const [error, setError] = useState<string | null>(null);

  const loadFeatureFlag = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const flag = await getFeatureFlag(api, flagKey);
      setIsEnabled(flag.active);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error loading feature flag';
      setError(errorMessage);
      setIsEnabled(false);
      Logger.error(`Error loading feature flag ${flagKey}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [api, flagKey]);

  useEffect(() => {
    if (isUserLogged) {
      void loadFeatureFlag();
    } else {
      setIsLoading(false);
    }
  }, [loadFeatureFlag, isUserLogged]);

  return {
    isEnabled,
    isLoading,
    error,
  };
};
